/**
 * AdminConsentModal Component
 *
 * Shared modal for multi-app admin consent flow.
 * Used by both TenantRegistrationForm (new tenant) and TenantManagementPage (re-consent).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Open20Regular, Copy20Regular, Checkmark20Regular } from '@fluentui/react-icons';
import { TenantApiService } from '../../services/tenantApi';
import { generateAdminConsentUrl } from '../../utils/adminConsent';
import { config } from '../../config';
import { Alert } from '../common/Alert';

const useStyles = makeStyles({
  dialogSurface: {
    minWidth: '700px',
    maxWidth: '700px',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  appSection: {
    ...shorthands.padding(tokens.spacingVerticalM),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    marginBottom: tokens.spacingVerticalM,
  },
  appSectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    marginBottom: tokens.spacingVerticalXS,
  },
  appSectionDescription: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    marginBottom: tokens.spacingVerticalM,
  },
  appSectionButtons: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  importantBox: {
    marginTop: tokens.spacingVerticalM,
    ...shorthands.padding(tokens.spacingVerticalM),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  statusMessage: {
    textAlign: 'center',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    margin: 0,
  },
  successMessage: {
    color: tokens.colorPaletteGreenForeground1,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: tokens.spacingVerticalL,
  },
});

/**
 * Admin consent app info
 */
export interface AdminConsentApp {
  clientId: string;
  name: string;
  description: string;
}

interface AdminConsentModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Azure tenant ID */
  tenantId: string;
  /** Tenant display name */
  tenantName: string;
  /** Array of apps requiring admin consent */
  apps: AdminConsentApp[];
  /** Callback when verification succeeds */
  onVerifySuccess: () => void;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Optional custom title (defaults to "Admin Consent for {tenantName}") */
  title?: string;
  /** Optional custom verify button text (defaults to "Verify Admin Consent") */
  verifyButtonText?: string;
  /** Optional custom verifying button text (defaults to "Verifying...") */
  verifyingButtonText?: string;
}

const POLLING_INTERVAL_MILLISECONDS = 10_000;
const MAX_RETRY_COUNT = 24;

export const AdminConsentModal: React.FC<AdminConsentModalProps> = ({
  isOpen,
  tenantId,
  tenantName,
  apps,
  title,
  verifyButtonText = 'Verify Admin Consent',
  verifyingButtonText = 'Verifying...',
  onVerifySuccess,
  onClose,
}) => {
  const styles = useStyles();

  // State
  const [appsWithActionTaken, setAppsWithActionTaken] = useState<Set<string>>(new Set());
  const [copiedAppId, setCopiedAppId] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [consentNotReady, setConsentNotReady] = useState(false);
  const [isWaitingForPropagation, setIsWaitingForPropagation] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [retriesExhausted, setRetriesExhausted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current !== null) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsWaitingForPropagation(false);
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      stopPolling();
      setAppsWithActionTaken(new Set());
      setCopiedAppId(null);
      setIsVerifying(false);
      setConsentNotReady(false);
      setRetryCount(0);
      setRetriesExhausted(false);
      setError(null);
    }
  }, [isOpen, stopPolling]);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // Listen for admin consent result from popup window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'admin-consent-result' && event.data?.success) {
        // Auto-trigger verification when consent is successful
        if (isOpen && appsWithActionTaken.size === apps.length) {
          handleVerify();
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, appsWithActionTaken, apps.length]);

  /**
   * Handle Grant Consent button click
   */
  const handleGrantConsent = (clientId: string) => {
    const url = generateAdminConsentUrl(tenantId, clientId, config.backendUrl);
    window.open(url, 'adminConsent', 'width=600,height=700,scrollbars=yes');
    setAppsWithActionTaken((prev) => new Set(prev).add(clientId));
  };

  /**
   * Handle Copy Link button click
   */
  const handleCopyLink = async (clientId: string) => {
    const url = generateAdminConsentUrl(tenantId, clientId, config.backendUrl);
    await navigator.clipboard.writeText(url);
    setCopiedAppId(clientId);
    setAppsWithActionTaken((prev) => new Set(prev).add(clientId));
    setTimeout(() => setCopiedAppId(null), 2000);
  };

  /**
   * Poll for enterprise app propagation
   */
  const startPolling = useCallback(() => {
    let currentRetry = 1;
    setRetryCount(1);
    setIsWaitingForPropagation(true);
    setRetriesExhausted(false);
    setConsentNotReady(true);

    pollingIntervalRef.current = setInterval(async () => {
      currentRetry++;
      setRetryCount(currentRetry);

      if (currentRetry > MAX_RETRY_COUNT) {
        stopPolling();
        setRetriesExhausted(true);
        setConsentNotReady(false);
        return;
      }

      try {
        const verification = await TenantApiService.verifyAccess(tenantId);
        if (verification.has_access) {
          stopPolling();
          setConsentNotReady(false);
          onVerifySuccess();
        }
      } catch {
        // Ignore errors during polling — keep retrying
      }
    }, POLLING_INTERVAL_MILLISECONDS);
  }, [tenantId, onVerifySuccess, stopPolling]);

  /**
   * Handle Verify Admin Consent button click
   */
  const handleVerify = async () => {
    setIsVerifying(true);
    setError(null);
    setConsentNotReady(false);
    setRetriesExhausted(false);

    try {
      const verification = await TenantApiService.verifyAccess(tenantId);

      if (!verification.has_access) {
        setIsVerifying(false);
        startPolling();
        return;
      }

      // Success - call callback
      onVerifySuccess();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to verify admin consent';
      setError(errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  const allAppsHaveAction = appsWithActionTaken.size === apps.length && apps.length > 0;

  return (
    <Dialog open={isOpen}>
      <DialogSurface className={styles.dialogSurface} data-testid="admin-consent-modal">
        <DialogBody>
          <DialogTitle>{title ?? `Admin Consent for ${tenantName}`}</DialogTitle>
          <DialogContent className={styles.dialogContent}>
            {/* Waiting for propagation - auto-retry in progress */}
            {isWaitingForPropagation && consentNotReady && (
              <Alert intent="warning" data-testid="propagation-waiting-message">
                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                  <Spinner size="tiny" />
                  <span>
                    Waiting for enterprise apps to propagate... (attempt {retryCount} of {MAX_RETRY_COUNT})
                  </span>
                </div>
              </Alert>
            )}

            {/* Retries exhausted */}
            {retriesExhausted && (
              <Alert intent="error" data-testid="propagation-exhausted-message">
                The enterprise apps have not propagated after {MAX_RETRY_COUNT} attempts. Please wait a few minutes and try again, or verify that admin consent was granted successfully.
              </Alert>
            )}

            {/* Error Message */}
            {error && (
              <Alert intent="error" data-testid="consent-error-message">
                {error}
              </Alert>
            )}

            {/* Introduction */}
            <p>
              To grant access to this tenant, a tenant administrator must grant admin consent
              for the following applications. This one-time authorization allows IRPilot to securely manage
              users and security settings in the tenant.
            </p>

            <p style={{ marginBottom: tokens.spacingVerticalS }}>
              Click each button below to grant consent for each application:
            </p>
            <ol style={{ margin: 0, paddingLeft: tokens.spacingHorizontalXL }}>
              <li>Sign in with a <strong>Global Administrator</strong> account</li>
              <li>Review the requested permissions</li>
              <li>Click <strong>Accept</strong> to grant consent</li>
            </ol>

            <div className={styles.importantBox}>
              <strong>Important:</strong>
              <ul style={{ margin: 0, marginTop: tokens.spacingVerticalXS, paddingLeft: tokens.spacingHorizontalL }}>
                <li>Only a Global Administrator can grant admin consent</li>
                <li>It may take 1-2 minutes for the enterprise application to appear after granting consent</li>
                <li>You must grant consent for <strong>all</strong> applications listed below</li>
              </ul>
            </div>

            {/* App sections */}
            <div style={{ marginTop: tokens.spacingVerticalL }}>
              {apps.map((app) => (
                <div key={app.clientId} className={styles.appSection}>
                  <div className={styles.appSectionTitle}>{app.name}</div>
                  <div className={styles.appSectionDescription}>{app.description}</div>
                  <div className={styles.appSectionButtons}>
                    <Button
                      appearance="primary"
                      icon={<Open20Regular />}
                      onClick={() => handleGrantConsent(app.clientId)}
                      disabled={isVerifying || isWaitingForPropagation}
                      data-testid={`grant-consent-${app.clientId}`}
                    >
                      Grant Consent
                    </Button>
                    <Button
                      appearance="secondary"
                      icon={copiedAppId === app.clientId ? <Checkmark20Regular /> : <Copy20Regular />}
                      onClick={() => handleCopyLink(app.clientId)}
                      disabled={isVerifying || isWaitingForPropagation}
                      data-testid={`copy-link-${app.clientId}`}
                    >
                      {copiedAppId === app.clientId ? 'Copied!' : 'Copy Link'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Divider */}
            <Divider style={{ marginTop: tokens.spacingVerticalXL, marginBottom: tokens.spacingVerticalL }} />

            {/* Status message */}
            {allAppsHaveAction ? (
              <p className={`${styles.statusMessage} ${styles.successMessage}`} data-testid="consent-status-message">
                Click the button below to verify that admin consent was successful.
              </p>
            ) : (
              <p className={styles.statusMessage}>
                After granting consent for all applications, click the button below to verify.
              </p>
            )}

            {/* Verify button */}
            <div className={styles.buttonContainer}>
              <Button
                appearance="primary"
                onClick={handleVerify}
                disabled={!allAppsHaveAction || isVerifying || isWaitingForPropagation}
                icon={isVerifying ? <Spinner size="tiny" /> : undefined}
                data-testid="verify-consent-button"
              >
                {isVerifying ? verifyingButtonText : isWaitingForPropagation ? 'Waiting...' : verifyButtonText}
              </Button>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={onClose}
              disabled={isVerifying}
              data-testid="close-consent-dialog-button"
            >
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
